import { Container } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import { createContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CounterCheckModal from '../components/popups/CounterCheckModal';
import { useCalculatorState } from '../hooks/useCalculatorState.js';
import CalculatorLayout from '../layouts/CalculatorLayout.js';

export const StageContext = createContext();
export const OverallDataContext = createContext();
export const TabContext = createContext();

export default function CalculatorPage() {
    const { state } = useLocation();
    const {
        formID,
        calcStage, setStage,
        metadata, setMetadata,
        inputData, setInputData,
        placeholderData, setPlaceholderData,
        outputData, setOutputData,
        inputCarbonOffset, setInputCarbonOffset,
        selectedFootprintCategories, setFootprintCategories,
        counterData, setCounterData,
        openCounterCheck, setCounterCheckOpen,
        errors, setErrors,
        openError, setOpenError,
        inputReadOnly, setReadOnly,
        isLoading,
        errorMsg,
        handleSubmissionPrep,
        checkCounterDataPass,
    } = useCalculatorState();
    
    // For handling current tab
    const [tabValue, setTabValue] = useState(0);
    const [tagToTabMap, setTagToTabMap] = useState({});
    const changeTabTo = (value) => {
        if (value) setTabValue(value);
    }

    // // For handling error alerts (using <Snackbar> component)
    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }
        setOpenError(false);
    };

    // Submit Process Start: Handler function (onClick action called by submit button)
    const handleSubmitButtonPress = (toSave=false) => {
        if (calcStage === 1 || checkCounterDataPass()) handleSubmissionPrep(toSave);
        else setCounterCheckOpen(true);
    };

    // Location state handler for saved sessions
    useEffect(() => {
        if (state) {
            setStage(state?.calcStage);
            setInputData(state?.inputs);
            setPlaceholderData(state?.inputs);
            setFootprintCategories(state?.inputs?.selected);
            setOutputData(state?.outputs);
            setMetadata(state?.metadata);
        }
    }, [state]);

    useEffect(() => {
        const handleBeforeUnload = (ev) => ev.preventDefault();
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    return (
        <Box className="light-background remove-background-print" sx={{ height: '100%', flexGrow: '1' }}>
            <Container
                maxWidth={false}
                sx={{
                    maxWidth: {
                        lg: 'lg',
                        xl: '60%',
                    },
                }}
            >
                <TabContext.Provider value={{ 
                    tabValue: [tabValue, setTabValue], 
                    tagToTabMap: [tagToTabMap, setTagToTabMap], 
                    changeTabTo: changeTabTo 
                }}>
                    <OverallDataContext.Provider value={{
                        formID: formID,
                        calcStage: [calcStage, setStage],
                        metadata: [metadata, setMetadata], 
                        inputData:[inputData, setInputData], 
                        placeholderData: placeholderData,
                        outputData:[outputData, setOutputData], 
                        inputCarbonOffset:[inputCarbonOffset, setInputCarbonOffset], 
                        selectedFootprintCategories: [selectedFootprintCategories, setFootprintCategories],
                        counterData: [counterData, setCounterData], 
                        errorData: [errors, setErrors], 
                        isLoading: isLoading,
                        inputReadOnly: [inputReadOnly, setReadOnly],
                        handleSubmitButtonPress: handleSubmitButtonPress,
                    }} >
                        <CalculatorLayout />
                        {
                            (calcStage !== 1) &&
                            <CounterCheckModal open={openCounterCheck} setOpen={setCounterCheckOpen} handleSubmit={handleSubmissionPrep} />
                        }
                    </OverallDataContext.Provider>
                </TabContext.Provider>
            </Container>
            
            <Snackbar open={(openError && Boolean(errorMsg))} autoHideDuration={8000} onClose={handleClose}>
                <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }} elevation={6} variant="filled">
                    {errorMsg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
